import { notEmpty, useFallbackStorageValue } from "@product/scmp-sdk";
import { useMountEffect } from "@react-hookz/web";
import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useMemo } from "react";

import { homeTypeformAtom, TypeformUtmSource } from "~/components/app-bar/typeform-button/atoms";
import { useSCMPExperimentCookie } from "~/components/home/hooks";
import { accountAtom } from "~/lib/account";
import { useScmpWidgetConfig } from "~/lib/app/hooks";

import { HomepageImpressionCount, HomepageSurveyLastSeenAtLocalStorageKey } from "./consts";

export const useHomepageImpressionCount = (isHomepage: boolean) => {
  const { set: setHomepageImpressionCount, value: homepageImpressionCount } =
    useFallbackStorageValue(HomepageImpressionCount);

  const { value: lastSeenAt } = useFallbackStorageValue(HomepageSurveyLastSeenAtLocalStorageKey);

  useMountEffect(() => {
    const homepageImpressionCountNumber = Number(homepageImpressionCount);
    if (!notEmpty(lastSeenAt) && isHomepage) {
      setHomepageImpressionCount(homepageImpressionCountNumber + 1);
    }
  });

  return homepageImpressionCount;
};

const useGetIsConfigEnabled = (controlEnabled: boolean, variantEnabled: boolean) => {
  const { value } = useSCMPExperimentCookie();

  switch (value) {
    case "a":
      return controlEnabled;
    case "b":
      return variantEnabled;
    case "x":
      return false;
  }
  return false;
};

const useIsHomepageTypeformEnabled = () => {
  const { scmpWidgetConfigState } = useScmpWidgetConfig();
  const homepageSurveyConfig = scmpWidgetConfigState?.result?.homepage_survey;
  const controlEnabled = homepageSurveyConfig?.controlEnabled ?? false;
  const variantEnabled = homepageSurveyConfig?.variantEnabled ?? false;
  const impressionCount = homepageSurveyConfig?.impressionCount ?? 0;

  const isConfigEnabled = useGetIsConfigEnabled(controlEnabled, variantEnabled);

  const { value: homepageImpressionCount } = useFallbackStorageValue(HomepageImpressionCount);

  const isHomepageTypeformEnabled =
    Number(homepageImpressionCount) >= impressionCount && isConfigEnabled;

  return isHomepageTypeformEnabled;
};

export const useIsShowHomepageTypeform = () => {
  const { value: lastSeenAt } = useFallbackStorageValue(HomepageSurveyLastSeenAtLocalStorageKey);
  const isHomepageTypeformEnabled = useIsHomepageTypeformEnabled();
  const setHomeTypeformState = useSetAtom(homeTypeformAtom);
  const shouldShowHomepageTypeform = !notEmpty(lastSeenAt) && isHomepageTypeformEnabled;

  const getShowHomepageTypeformHandler = useCallback(
    (typeformCampaignId: number, typeformUtmSource: TypeformUtmSource) => {
      if (shouldShowHomepageTypeform) {
        setHomeTypeformState({
          isOpenTypeform: true,
          typeformCampaignId,
          typeformUtmSource,
        });
      }
    },
    [shouldShowHomepageTypeform, setHomeTypeformState],
  );

  return { getShowHomepageTypeformHandler, shouldShowHomepageTypeform };
};

const useGetUtmCampaignPrefix = () => {
  const { value } = useSCMPExperimentCookie();
  switch (value) {
    case "a":
      return "control";
    case "b":
      return "variant";
    case "x":
      return null;
  }
  return null;
};

export const useTypeformHiddenFields = () => {
  const { user } = useAtomValue(accountAtom);
  const userUuid = user?.uuid ?? "";
  const homeTypeformState = useAtomValue(homeTypeformAtom);
  const utmCampaignPrefix = useGetUtmCampaignPrefix();
  const utmCampaign = `${utmCampaignPrefix}_${homeTypeformState?.typeformCampaignId}`;
  const utmSource: TypeformUtmSource =
    homeTypeformState?.typeformUtmSource ?? TypeformUtmSource.desktop;
  return useMemo(
    () => ({
      utm_campaign: utmCampaign,
      utm_source: utmSource,
      utm_term: userUuid,
    }),
    [utmCampaign, userUuid, utmSource],
  );
};

export const useHandleSurveyFinished = () => {
  const { set: setLastSeenAt } = useFallbackStorageValue(HomepageSurveyLastSeenAtLocalStorageKey);

  const handleSurveyFinished = useCallback(() => {
    setLastSeenAt(Date.now());
  }, [setLastSeenAt]);

  return handleSurveyFinished;
};
